/* Email provider catalogue for the admin UI — the same contracts/email-providers.json the Go API validates against. */
import PROVIDERS_JSON from "../../../../../contracts/email-providers.json";
import type { EmailConfig, EmailProviderId, EmailProviderInput, EmailProviderSettings, SmtpSecurity } from "../../../data/types";

export interface Region { id: string; label: string; labelAr?: string; host?: string; apiBase?: string; spf?: string }
export interface ProviderInfo {
  id: EmailProviderId; name: string; nameAr: string; kind: "smtp" | "api" | "log"; initials: string; tone: string;
  fields: string[]; optional?: string[]; labels: Record<string, [string, string]>;
  defaults?: { host?: string; port?: number; security?: SmtpSecurity; authMethod?: "plain" | "login"; region?: string; messageStream?: string };
  regions?: Region[]; regionFree?: boolean; hostTemplate?: string; ports?: { port: number; security: SmtpSecurity }[];
  spf: string; dkim: [string, string]; notes: [string, string][];
  /** Where to sign up, where the credentials are created, domain verification and sandbox notes (official links only). */
  setup: { steps: [string, string][]; links: { label: [string, string]; url: string }[] };
}
export const PROVIDERS = (PROVIDERS_JSON as unknown as { providers: ProviderInfo[] }).providers;
export const providerById = (id: string) => PROVIDERS.find((p) => p.id === id);

/** Editable provider form (secret is write-only: empty = keep the stored one). */
export interface ProviderDraft { provider: EmailProviderId; host: string; port: string; security: SmtpSecurity | ""; authMethod: "plain" | "login" | ""; username: string; secret: string; region: string; domain: string; messageStream: string }

export function draftFrom(p: EmailProviderSettings | null | undefined): ProviderDraft {
  return { provider: p?.provider ?? "log", host: p?.host ?? "", port: p?.port ? String(p.port) : "", security: p?.security ?? "", authMethod: p?.authMethod ?? "", username: p?.username ?? "",
    secret: "", region: p?.region ?? "", domain: p?.domain ?? "", messageStream: p?.messageStream ?? "" };
}

/** Choosing a provider applies its presets (region, port, security, auth). */
export function withProvider(d: ProviderDraft, id: EmailProviderId): ProviderDraft {
  const p = providerById(id);
  const df = p?.defaults ?? {};
  const preset = presetHost(id, df.region ?? "");
  return { provider: id, host: p?.fields.includes("host") ? (d.provider === id ? d.host : preset) : "", port: df.port ? String(df.port) : "", security: df.security ?? "", authMethod: df.authMethod ?? "",
    username: d.provider === id ? d.username : "", secret: "", region: df.region ?? "", domain: d.provider === id ? d.domain : "", messageStream: df.messageStream ?? "" };
}

/** Host preset for a provider/region (used to pre-fill an editable host). */
export function presetHost(id: string, region: string): string {
  const p = providerById(id);
  if (!p) return "";
  if (p.hostTemplate) return p.hostTemplate.replace("{region}", region);
  return p.regions?.find((r) => r.id === region)?.host ?? p.defaults?.host ?? "";
}

/** Effective SMTP host shown for presets (mirrors ProviderConfig.Normalize). */
export function effectiveHost(d: ProviderDraft): string {
  const p = providerById(d.provider);
  if (!p || p.kind !== "smtp") return "";
  if (p.fields.includes("host")) return d.host;
  if (p.hostTemplate) return p.hostTemplate.replace("{region}", d.region);
  if (p.defaults?.host) return p.defaults.host;
  return p.regions?.find((r) => r.id === d.region)?.host ?? "";
}

export function toInput(d: ProviderDraft): EmailProviderInput {
  const p = providerById(d.provider);
  const f = (k: string) => !!p?.fields.includes(k);
  const out: EmailProviderInput = { provider: d.provider };
  if (f("host")) out.host = d.host.trim();
  if (p?.kind === "smtp") {
    out.port = Number(d.port) || 0;
    if (d.security) out.security = d.security;
    if (d.authMethod) out.authMethod = d.authMethod;
  }
  if (f("username")) out.username = d.username.trim();
  if (f("secret") && d.secret) out.secret = d.secret;
  if (f("region")) out.region = d.region.trim();
  if (f("domain")) out.domain = d.domain.trim();
  if (f("messageStream")) out.messageStream = d.messageStream.trim();
  return out;
}

export interface EmailDraft { provider: ProviderDraft; useFallback: boolean; fallback: ProviderDraft; fromName: string; fromAddress: string; replyTo: string; sandboxTo: string; dailyLimit: string }
export function emailDraft(c: EmailConfig): EmailDraft {
  return { provider: draftFrom(c.provider), useFallback: !!c.fallback, fallback: c.fallback ? draftFrom(c.fallback) : withProvider(draftFrom(null), "ses"),
    fromName: c.fromName, fromAddress: c.fromAddress, replyTo: c.replyTo, sandboxTo: c.sandboxTo, dailyLimit: c.dailyLimit ? String(c.dailyLimit) : "" };
}
